// NPM
import Joi from "joi";
import moment from "moment";

// Interfaces
import IPatientDetails from "../interfaces/IPatientDetails";

export default class Patient {
	public static LIST_OF_SEXES: string[] = ["Female", "Male", "Non-binary"];

	public static schema = Joi.object({
		givenName: Joi.string()
			.min(2)
			.required()
			.error(new Error("givenNameError|Given name required.")),
		middleName: Joi.string().allow("").optional(),
		familyName: Joi.string()
			.min(2)
			.required()
			.error(new Error("familyNameError|Family name required.")),
		yearOfBirth: Joi.string()
			.pattern(new RegExp("((1|2)[0-9]{3})"))
			.required()
			.error(new Error("yearOfBirthError|Year of birth required.")),
		monthOfBirth: Joi.string()
			.pattern(new RegExp("(0[1-9]|1[0-2])"))
			.required()
			.error(new Error("monthOfBirthError|Month of birth required.")),
		dateOfBirth: Joi.string()
			.pattern(new RegExp("(0[1-9]|(1|2)[0-9]|3[0-1])"))
			.required()
			.error(new Error("dateOfBirthError|Date of birth required.")),
		mobile: Joi.string()
			.min(11)
			.required()
			.error(new Error("mobileError|Mobile required.")),
		notes: Joi.string()
			.required()
			.min(2)
			.max(255)
			.error(
				new Error(
					"notesError|Reason to visit cannot be empty or longer than 255 characters."
				)
			),
		attendancePreference: Joi.string()
			.valid("telehealth", "on-site")
			.required(),
		mobileFormat: Joi.string().valid("aus", "intl").required(),
		isTelehealth: Joi.boolean().required(),
		isNewPatient: Joi.boolean().required(),
		isPersonBooking: Joi.boolean().required(),
		email: Joi.string().allow("").optional(),
		isStudent: Joi.boolean().required(),
		institutionName: Joi.string().allow("").optional(),
		studentNumber: Joi.string().allow("").optional(),
		studentNumberExpiry: Joi.string().allow("").optional(),
		hasMedicare: Joi.boolean().required(),
		hasConcession: Joi.boolean().required(),
		sex: Joi.string()
			.required()
			.valid(...Patient.LIST_OF_SEXES)
			.error(new Error("sexError|Please select an option.")),
		gender: Joi.string().allow("").optional(),
		telehealthRestrictionAcknowledged: Joi.boolean().optional(),
	});

	/**
	 * This function calculates the age of a person based on their date of birth and returns a string in years and months or just months if the person is less than 1 year old
	 *
	 *
	 * @param dob the date of birth
	 * @return the string of the age
	 */

	public static calcAge(dob: string): string {
		const now = moment();
		const months = now.diff(moment(dob), "months");

		const years = Math.floor(months / 12);
		const remainingMonths = months % 12;

		if (years === 0) {
			return `${remainingMonths} months old`;
		}

		return `${years} years and ${remainingMonths} months old`;
	}

	public static formatDob(patientDetails: IPatientDetails): string {
		return `${patientDetails.yearOfBirth}-${patientDetails.monthOfBirth}-${patientDetails.dateOfBirth}`;
	}

	public static mobileNumberIsValid(mob: string): boolean {
		return mob.length > 11;
	}

	public static generateFullName(
		patientData: IPatientDetails,
		withMiddleName?: boolean
	): string {
		if (withMiddleName) {
			return `${patientData.givenName} ${patientData.middleName} ${patientData.familyName}`;
		}

		return `${patientData.givenName} ${patientData.familyName}`;
	}
}
